import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import InfoTip from '@/components/atoms/InfoTip';
import { TrendIcon } from '@/components/atoms/TrendIcon';
// import { getIcon, COLOR_NEUTRAL } from 'utils/fishHealth';
import { CircularProgressBar } from '@/components/molecules/CircularProgressBar';
import { alertIconColors, graphSettings } from '@/config/settings';
import { formatFractionAsPercentageByLocale } from '@/utils/localeFormatters';

import styles from './FishHealthSummary.module.scss';

interface FishHealthSummaryProps {
    label?: string;
    /** A share (0–1); null when there is no estimate, shown as a dash. */
    dataRate: number | null;
    description?: string;
    downgradeRate?: number;
    variant?: 'default' | 'progress' | 'card' | 'inline';
    status: 'positive' | 'negative' | 'neutral';
    direction: 'up' | 'down' | 'flat';
}

function FishHealthSummary({
    label = '',
    dataRate,
    downgradeRate,
    variant = 'progress',
    direction,
    status,
}: FishHealthSummaryProps) {
    const { t, i18n } = useTranslation();
    // Any part on screen counts as visible. Without IntersectionObserver the summary stays
    // visible, as it starts.
    const { ref: visibilityRef, inView: isVisible } = useInView({
        initialInView: true,
        fallbackInView: true,
    });

    const suffixes = new Map([
        ['wound', 's'],
        ['scale loss', 'es'],
        ['maturation', 's'],
    ]);

    const trend = new Map([
        ['negative', 'increasing'],
        ['positive', 'decreasing'],
        ['neutral', 'stable'],
    ]);

    // The status tokens lift the neutral blue on the dark card; the negative red keeps
    // production's value in both themes.
    const trendColor: Record<FishHealthSummaryProps['status'], string> = {
        negative: graphSettings.statusColors.negative,
        positive: alertIconColors.success,
        neutral: alertIconColors.neutral,
    };

    return (
        <div className={styles.fishHealthSummary} data-variant={variant}>
            {label.length > 0 && variant !== 'inline' && (
                <div data-area="heading">
                    <h3>
                        {t(`atoms.fishHealthSummary.headings.${label}`, { defaultValue: label })}
                        <TrendIcon
                            direction={direction}
                            disabled={false}
                            status={status}
                            size={32}
                        />
                    </h3>
                </div>
            )}
            <div data-area="currentNumber">
                <div data-area="animation">
                    <span ref={visibilityRef} className={`${'animFadeIn'}`}>
                        {variant === 'default' || variant === 'inline' ? (
                            <span className={isVisible ? styles.rotatingBg : styles.focusBg}>
                                {formatFractionAsPercentageByLocale(dataRate, i18n.language)}
                            </span>
                        ) : (
                            <span>
                                <CircularProgressBar
                                    percentage={(dataRate ?? 0) * 100}
                                    color={trendColor[status]}
                                    size={100}
                                />
                            </span>
                        )}
                    </span>
                </div>

                <div data-area="description">
                    <p>
                        {variant !== 'inline' && (
                            <em>{`${formatFractionAsPercentageByLocale(dataRate, i18n.language)} `}</em>
                        )}
                        {`${t('atoms.fishHealthSummary.ofAllTheFishHave')} ${t(
                            `fishHealthIndicators.${label}`,
                            { count: 500 }
                        ).toLowerCase()}.`}
                    </p>
                </div>
            </div>
            <p>
                {`${t('atoms.fishHealthSummary.theTrendIs')} ${t(`fishHealthColorStatus.${trend.get(status)}Text`)}${
                    downgradeRate ? ' ' : '.'
                } `}
                {variant === 'inline' && (
                    <TrendIcon direction={direction} disabled={false} status={status} size={32} />
                )}
            </p>

            {downgradeRate !== undefined && (
                <p>
                    {`${t('atoms.fishHealthSummary.estimatedDowngradesBasedOnWounds')}:`}
                    <strong>
                        {formatFractionAsPercentageByLocale(downgradeRate, i18n.language)}{' '}
                        <InfoTip
                            topic={t('atoms.fishHealthSummary.estimatedDowngradesBasedOnWounds')}
                            content={t('atoms.fishHealthSummary.downgradeDescription')}
                            size="inherit"
                            placement="bottom-end"
                            maxWidth={150}
                        />
                    </strong>
                </p>
            )}
        </div>
    );
}

export default FishHealthSummary;
export { FishHealthSummary };
